import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { useOnClickOutside } from '@hooks/useOnClickOutside';
import { useUniversalModalDispatch } from '@components/context/UniversalModalContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import {
    User,
    useAccountUsersQuery,
    useContactableCourseMemberGroupTypesQuery,
    useContactableUsersQuery,
    useCreateConversationMutation,
} from '@graphql/generated-graphql';
import { getClient } from '@graphql/graphql-client/graphClient';
import { Button, Input } from '@skipper/ui';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { SelectPeopleDropdown } from '@components/modals/chatModal/ChatComponents';

export const CreateChatModal = ({ user = { id: null } }: { user?: User }) => {
    const { toggle, dispatchNotification } = useUniversalModalDispatch();
    const [selectedUsers, setSelectedUsers] = useState<any[]>(!!user.id ? [user] : []);
    const [selectedCourses, setSelectedCourses] = useState<any[]>([]);
    const [selectedSet, setSelectedSet] = useState<any[]>(
        !!user.id ? [{ type: 'user', element: user }] : [],
    );
    const queryClient = useQueryClient();

    const elementRef = useRef<HTMLDivElement | null>(null);
    useOnClickOutside(elementRef, () => {
        toggle(false);
    });

    const createChat = useCreateConversationMutation(getClient('communication'), {
        onSettled: async (data) => {
            if (data?.createConversation) {
                toggle(false);
                queryClient.invalidateQueries('conversationsList');
                dispatchNotification({
                    title: 'Chat was created successfully',
                    text: '',
                    type: 'success',
                });
            } else {
                dispatchNotification({
                    title: 'Create Chat Error',
                    text: 'Something went wrong',
                    type: 'danger',
                });
            }
        },
    });

    const { data, isLoading, isFetching } = useContactableUsersQuery(
        getClient('communication'),
        {},
        { refetchOnWindowFocus: false },
    );

    const { data: coursesData, isLoading: coursesLoading } =
        useContactableCourseMemberGroupTypesQuery(getClient('communication'));

    const courseMemberGroupTypes = coursesData?.contactableCourseMemberGroupTypes;

    const { data: userData } = useAccountUsersQuery(getClient('users'), {
        includeInactive: true,
    });

    const childUsers = data?.contactableUsers?.filter(
        (user) =>
            user?.accountRole.roleType === 'child' &&
            !userData?.accountUsers?.find((u) => u.id === user.id),
    );

    const adultUsers = data?.contactableUsers?.filter(
        (user) =>
            user?.accountRole.roleType === 'adult' &&
            !userData?.accountUsers?.find((u) => u.id === user.id),
    );

    const initialValues = {
        participatingUserIds: [],
        participatingCourses: [],
        iconFileEntryId: null,
        title: '',
        message: '',
        messageSubject: '',
    };

    const methods = useForm<any>({
        defaultValues: initialValues,
        mode: 'all',
    });

    const successCallback = useCallback(
        (vals: any) => {
            createChat.mutate({
                participatingUserIds: selectedSet
                    .filter((el) => el.type === 'user')
                    .map((el) => el.element.id),
                participatingCourses: selectedSet
                    .filter((el) => el.type === 'course')
                    .map((el) => {
                        return {
                            courseId: el.element.courseId,
                            memberGroupTypes: el.element.selectedMemberGroupTypes,
                        };
                    }),
                iconFileEntryId: null,
                title: '',
                message: vals?.chatMessage,
                messageSubject: vals?.emailSubject,
            });
        },
        [selectedSet, createChat],
    );

    const {
        reset,
        formState: { isSubmitSuccessful },
    } = methods;

    return (
        <div className="fixed inset-0 overflow-y-auto flex h-full">
            <div className="relative flex-grow flex-1 flex justify-center items-center py-2">
                <FormProvider {...methods}>
                    <div
                        className="relative bg-white rounded-md py-5 w-[370px] md:w-[550px] font-medium text-sm"
                        ref={elementRef}>
                        <div className="px-5">
                            <div className="flex justify-between items-center">
                                <h6 className="font-bold">New Chat</h6>
                                <FontAwesomeIcon
                                    icon={faXmark}
                                    size="lg"
                                    className="cursor-pointer"
                                    onClick={() => toggle(false)}
                                />
                            </div>
                            <SelectPeopleDropdown
                                familyData={userData?.accountUsers}
                                childUsers={childUsers}
                                adultUsers={adultUsers}
                                selectedUsers={selectedUsers}
                                selectedSet={selectedSet}
                                setSelectedSet={setSelectedSet}
                                setSelectedUsers={setSelectedUsers}
                                courseMemberGroupTypes={courseMemberGroupTypes}
                                selectedCourses={selectedCourses}
                                setSelectedCourses={setSelectedCourses}
                            />
                            <div className="text-sm mt-20">
                                <MessageArea />
                            </div>
                            <div className="mt-4">
                                <EmailSubjectInput />
                            </div>
                        </div>
                        <div className="px-5 mt-16 border-t">
                            <Button
                                variant="magenta"
                                size="medium"
                                className="mt-4 w-full"
                                disabled={selectedSet.length === 0 || createChat.isLoading}
                                onClick={methods.handleSubmit(successCallback)}>
                                Create chat
                            </Button>
                        </div>
                    </div>
                </FormProvider>
            </div>
        </div>
    );
};

const MessageArea = () => {
    const { register, control } = useFormContext();
    const chatField = register('chatMessage');
    return (
        <textarea
            id="chatMessage"
            {...chatField}
            placeholder="Type your message here"
            className="w-full border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-600 p-4 disabled:bg-gray-50"
            rows={8}
            cols={60}></textarea>
    );
};

const EmailSubjectInput = () => {
    const { register, control } = useFormContext();
    const emailField = register('emailSubject');
    return (
        <Input
            type="text"
            id="emailSubject"
            height="medium"
            placeholder="Email notification subject title"
            {...emailField}
        />
    );
};
